import { env } from '../../../env.mjs';

const PORTFOLIO_USERNAME_MIN_LENGTH = 3;
const PORTFOLIO_USERNAME_MAX_LENGTH = 30;
const PORTFOLIO_USERNAME_PATTERN = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

export const PORTFOLIO_RESERVED_USERNAMES = [
  'admin',
  'api',
  'app',
  'application',
  'auth',
  'externships',
  'gift',
  'home',
  'login',
  'maintenance',
  'my-account',
  'payment',
  'portfolio',
  'portfolios',
  'recommendations',
  'sign-in',
  'sign-up',
  'sign-out',
  'sitemap',
  'robots',
  'u',
  'welcome-back',
] as const;

export type PortfolioUsernameValidationReason = 'too_short' | 'too_long' | 'invalid_format' | 'reserved';

export type PortfolioUsernameValidationResult =
  | { valid: true; normalizedUsername: string }
  | { valid: false; normalizedUsername: string; reason: PortfolioUsernameValidationReason };

export function normalizePortfolioUsername(value: string): string {
  return value.trim().toLowerCase();
}

export function isPortfolioUsernameReserved(username: string): boolean {
  const normalizedUsername = normalizePortfolioUsername(username);
  return PORTFOLIO_RESERVED_USERNAMES.includes(normalizedUsername as (typeof PORTFOLIO_RESERVED_USERNAMES)[number]);
}

export function validatePortfolioUsername(value: string): PortfolioUsernameValidationResult {
  const normalizedUsername = normalizePortfolioUsername(value);

  if (normalizedUsername.length < PORTFOLIO_USERNAME_MIN_LENGTH) {
    return { valid: false, normalizedUsername, reason: 'too_short' };
  }

  if (normalizedUsername.length > PORTFOLIO_USERNAME_MAX_LENGTH) {
    return { valid: false, normalizedUsername, reason: 'too_long' };
  }

  if (!PORTFOLIO_USERNAME_PATTERN.test(normalizedUsername)) {
    return { valid: false, normalizedUsername, reason: 'invalid_format' };
  }

  if (isPortfolioUsernameReserved(normalizedUsername)) {
    return { valid: false, normalizedUsername, reason: 'reserved' };
  }

  return { valid: true, normalizedUsername };
}

export function getPortfolioDisplayPath(username: string): string {
  return `/portfolios/${normalizePortfolioUsername(username)}`;
}

// `www` is deliberate: the apex `extern.com` 301s every path to `www.extern.com`.
export const DEFAULT_PORTFOLIO_PUBLIC_ORIGIN = 'https://www.extern.com';

/** The `/portfolios` namespace is owned by the frontend — env vars only configure the origin. */
export const PORTFOLIO_PUBLIC_PATH = '/portfolios';

export const DEFAULT_PORTFOLIO_PUBLIC_BASE_URL = `${DEFAULT_PORTFOLIO_PUBLIC_ORIGIN}${PORTFOLIO_PUBLIC_PATH}`;

const TRAILING_SLASHES = /\/+$/;

/**
 * Normalize a configured public origin/base into `origin + /portfolios`.
 * Uses the URL constructor so accidental `https:/host` values (accepted by Zod's
 * `z.url()` but rejected by backend URI validators) become `https://host`.
 */
export function resolvePortfolioPublicBaseUrl(configured: string): string {
  const normalized = new URL(configured);
  const path = normalized.pathname.replace(TRAILING_SLASHES, '');
  // Tolerate legacy env values that already include the /portfolios path.
  if (path === PORTFOLIO_PUBLIC_PATH || path.endsWith(PORTFOLIO_PUBLIC_PATH)) {
    return `${normalized.origin}${path}`;
  }

  return `${normalized.origin}${PORTFOLIO_PUBLIC_PATH}`;
}

export function getPortfolioPublicBaseUrl(): string {
  try {
    const configured = env.NEXT_PUBLIC_PORTFOLIO_PUBLIC_BASE_URL;

    if (configured) {
      return resolvePortfolioPublicBaseUrl(configured);
    }
  } catch {
    // Fall back to the production base when env is unavailable (e.g. unit tests).
  }

  return DEFAULT_PORTFOLIO_PUBLIC_BASE_URL;
}

export function getPortfolioPublicUrl(username: string): string {
  return `${getPortfolioPublicBaseUrl()}/${normalizePortfolioUsername(username)}`;
}

const URL_PROTOCOL_PATTERN = /^https?:\/\//;
const WWW_PREFIX_PATTERN = /^www\./;

/** Compact base for display next to a username input, e.g. `extern.com/portfolios/`. */
export function getPortfolioPublicUrlDisplayPrefix(): string {
  return `${getPortfolioPublicBaseUrl().replace(URL_PROTOCOL_PATTERN, '').replace(WWW_PREFIX_PATTERN, '')}/`;
}
